<template>
    <!-- Month-and-year fields (business start date): stores the 1st of the picked month.
         Gets the parent's attributes WITHOUT modelValue: LcField binds v-model to this component, and
         its YYYY-MM-DD string would override the { month, year } the month picker needs. -->
    <VueDatePicker v-if="configDates.monthPicker"
        v-bind="monthPickerAttrs"
        :model-value="customMonth"
        month-picker
        :format="displayMonth"
        :min-date="configDates.minDate"
        :max-date="configDates.maxDate"
        :year-range="configDates.yearRange"
        prevent-min-max-navigation
        :placeholder="placeHolderText"
        :state="pickerState"
        auto-apply
        :offset="60"
        :locale="lang === 'fr' ? 'fr-CA' : 'en-CA'"
        @update:model-value="holdMonthPick"
        @closed="commitMonthPick"
        @cleared="commitMonthPick"
    ></VueDatePicker>
    <VueDatePicker v-else v-model="custom"
        :model-value="value"
        :flow="configDates.calendarFlow"
        :format="'yyyy-MM-dd'"
        :min-date="configDates.minDate"
        :max-date="configDates.maxDate"
        :year-range="configDates.yearRange"
        :start-date="configDates.startDate"
        prevent-min-max-navigation
        :enable-time-picker="false"
        :placeholder="placeHolderText"
        :state="pickerState"
        :hide-navigation="['year','month']"
        auto-apply
        six-weeks
        :offset="60"
        :locale="lang === 'fr' ? 'fr-CA' : 'en-CA'"
        utc="preserve"
        :text-input="canType"
        :text-input-options="TYPED_DATE_OPTIONS"
        @update:model-value="handleBlur"
        @invalid-select="handleOutOfRange"
        v-bind="$attrs"
    ></VueDatePicker>
    <!-- v-bind="$attrs" LAST reproduces Vue's automatic fall-through exactly (it used to be applied
         after every prop above), so the full-date pickers (dob, pay dates) behave as before. -->
</template>
<script setup>
import { inject, computed, useAttrs, nextTick } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { monthValueFromDate, firstOfMonthFromValue, formatMonthYear } from '@/form/helpers/month-date.js';
import { parseTypedDate } from '@/form/helpers/typed-date.js';

// Attributes are forwarded explicitly in the template (inheritAttrs: false in the plain <script>
// block below; two different pickers share the one root slot).
const attrs = useAttrs();
const monthPickerAttrs = computed(() => {
    // eslint-disable-next-line no-unused-vars
    const { modelValue, 'onUpdate:modelValue': onUpdateModelValue, ...rest } = attrs;
    return rest;
});

const props = defineProps({
    minlength: { type: [Number, String], default: undefined },
    maxlength: { type: [Number, String], default: undefined },
    type: { type: String, default: undefined },
    placeholder: { type: String, default: '' },
    value: { type: String, default: undefined },
    name: { type: String, required: true },
    label: { type: String, default: '' },
    lang: { type: String, default: 'en' },
});
const emit = defineEmits(["update:model-value", "input", "blurFromDate"]);

const { getTranslatedText } = inject('app');

// --- Date config helpers ---

/**
 * Returns the number of days in a given month (1-indexed).
 * Used to clamp day values when adding months — e.g. Jan 31 + 3 months = Apr 30, not Apr 31.
 */
function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
}

/**
 * Returns a local-midnight Date object for use with VueDatePicker min/max/start props.
 * We pass Date objects (not strings) because JS parses ISO date strings as UTC midnight,
 * which causes off-by-one exclusions in western timezones when the datepicker compares
 * against local calendar dates.
 */
function localDate(year, month, day) {
    const clampedDay = Math.min(day, daysInMonth(year, month));
    return new Date(year, month - 1, clampedDay);
}

// Config dates use LOCAL time because the datepicker calendar operates in the
// user's local timezone. Using UTC here causes off-by-one errors in western
// timezones (e.g. UTC midnight April 7 = April 6 7pm EST, excluding April 7).
const now = new Date();
now.setHours(0, 0, 0, 0);

const todayYear = now.getFullYear();
const todayMonth = now.getMonth() + 1;
const todayDay = now.getDate();

const tomorrow = new Date(now);
tomorrow.setDate(tomorrow.getDate() + 1);
const tomorrowYear = tomorrow.getFullYear();
const tomorrowMonth = tomorrow.getMonth() + 1;
const tomorrowDay = tomorrow.getDate();

const maxAge = 100;
const minAge = 18;
const averageAge = 40;

// Payday maxDate: 3 months from today, day clamped to valid range for the target month
const payMaxMonth = todayMonth + 3;
const payMaxYear = payMaxMonth > 12 ? todayYear + 1 : todayYear;
const payMaxMonthNorm = payMaxMonth > 12 ? payMaxMonth - 12 : payMaxMonth;

const configDatesHelper = {
    dob: {
        minDate: localDate(todayYear - maxAge, 1, 1),
        maxDate: localDate(todayYear - minAge, todayMonth, todayDay),
        yearRange: [todayYear - maxAge, todayYear - minAge],
        startDate: localDate(todayYear - averageAge, 6, 1),
        calendarFlow: ['year', 'month', 'calendar'],
    },
    payday: {
        minDate: localDate(tomorrowYear, tomorrowMonth, tomorrowDay),
        maxDate: localDate(payMaxYear, payMaxMonthNorm, todayDay),
        yearRange: [todayYear, todayYear + 1],
        startDate: localDate(tomorrowYear, tomorrowMonth, tomorrowDay),
        calendarFlow: ['calendar'],
    },
    defaultWithoutRestrictions: {
        minDate: localDate(todayYear - 100, 1, 1),
        maxDate: localDate(todayYear + 1, todayMonth, todayDay),
        yearRange: [todayYear - 100, todayYear + 1],
        startDate: localDate(todayYear - 3, 6, 1),
        calendarFlow: ['year', 'month', 'calendar'],
    },
    // Month and year only, latest pickable month = the current one (no future start dates).
    bizStart: {
        minDate: localDate(todayYear - 100, 1, 1),
        maxDate: localDate(todayYear, todayMonth, todayDay),
        yearRange: [todayYear - 100, todayYear],
        startDate: localDate(todayYear - 3, 6, 1),
        calendarFlow: ['year', 'month', 'calendar'],
        monthPicker: true,
    },
};

// --- Typing a date ---

// Where there is a mouse, the box also takes a typed date ("1985-06-15"); people tried to type
// their date of birth and nothing happened. Touch screens keep the calendar only: the keyboard
// would cover it, and the year -> month -> day picks are quick there.
const canType = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: fine)').matches;

// Enter or Tab keeps the typed date, as does leaving the box (auto-apply).
const TYPED_DATE_OPTIONS = { format: parseTypedDate };

// --- Computed ---

// LcField marks a field showing an error with aria-invalid; the picker's own `state` prop is
// what turns its input border red (false = invalid, null = no state).
const pickerState = computed(() => attrs['aria-invalid'] === 'true' ? false : null);

const placeHolderText = computed(() => {
    // where dates can be typed, show the format to type them in
    const fallback = canType ? '__date_format__' : '__select_date__';
    const placeholderText = props.placeholder && props.placeholder.length > 0 ? props.placeholder : fallback;
    return getTranslatedText(placeholderText, 'fields.placeholders');
});

const configDates = computed(() => {
    const fieldName = `${props.name}`;
    switch (fieldName) {
        case 'pay_date1':
        case 'pay_date2':
            return configDatesHelper.payday;
        case 'dob':
            return configDatesHelper.dob;
        case 'business_start_date':
            return configDatesHelper.bizStart;
        default:
            return configDatesHelper.defaultWithoutRestrictions;
    }
});

const getFormattedDate = computed(() => {
    const strDate = props.value;

    if (!strDate || strDate.length !== 10) {
        return null;
    }

    // Use Date.UTC() to keep the date in UTC throughout.
    // Previously used `new Date(year, month, day)` which creates a LOCAL date,
    // then .toISOString() shifted it to UTC — causing ±1 day errors depending on timezone.
    const [year, month, day] = strDate.split('-');
    const dateObject = new Date(Date.UTC(+year, month - 1, +day));

    if (isNaN(dateObject.getTime())) {
        return null;
    }

    return dateObject.toISOString().substring(0, 10);
});

const custom = computed({
    get() { return getFormattedDate.value; },
    set(datepickerValue) { emit('input', setFormattedDate(datepickerValue)); },
});

// Month-picker mode: the picker works with { month, year }; the field keeps a YYYY-MM-01 string.
const customMonth = computed(() => monthValueFromDate(props.value));

const displayMonth = (date) => formatMonthYear(date, props.lang);

// --- Methods ---

// Formats the picker's emitted value into a YYYY-MM-DD string.
// Uses UTC methods because the picker emits UTC values (utc="preserve" prop).
function setFormattedDate(rawDate) {
    if (!rawDate) {
        return null;
    }

    const d = new Date(rawDate);
    if (isNaN(d.getTime())) {
        return null;
    }

    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function handleBlur(modelData) {
    emit('blurFromDate', setFormattedDate(modelData));
}

// The picker refuses a typed date outside its range (under 18, a pay date in the past) without a
// word, and the box empties again. Keep the date as the answer instead, so the field's own message
// says what is wrong with it. The picker hands over its own local date here, not a UTC one.
function handleOutOfRange(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return;
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    emit('blurFromDate', `${date.getFullYear()}-${month}-${day}`);
}

// The month picker also sends a value when only a YEAR is picked: that year with the month on
// screen, so picking 2019 in October filled in October 2019 before any month was chosen. A pick
// only counts if the menu closes right after it, which a month pick does in the same tick (clearing
// says so itself); a year pick leaves the menu open for the month, and its value is dropped.
let pendingMonthPick;

function holdMonthPick(monthValue) {
    pendingMonthPick = monthValue;
    nextTick(() => {
        pendingMonthPick = undefined;
    });
}

function commitMonthPick() {
    if (pendingMonthPick === undefined) {
        return;
    }
    const value = firstOfMonthFromValue(pendingMonthPick);
    pendingMonthPick = undefined;
    emit('input', value);
    emit('blurFromDate', value);
}
</script>
<script>
// Forward attributes manually (see the template): automatic fall-through would hand the month
// picker the parent's v-model string.
export default { inheritAttrs: false };
</script>
